import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  content: { maxWidth: '1100px', margin: '32px auto', padding: '0 20px', width: '100%' },
  title: { fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginBottom: '24px', fontFamily: "'DM Sans',sans-serif" },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '24px', marginBottom: '24px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
  group: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", letterSpacing: '0.05em' },
  input: { padding: '8px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", outline: 'none' },
  select: { padding: '8px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", outline: 'none' },
  btnRow: { display: 'flex', justifyContent: 'center', marginTop: '20px' },
  btn: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '10px 32px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  summaryItem: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px' },
  summaryLabel: { fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: '4px' },
  summaryValue: { fontSize: '18px', fontWeight: 700, color: 'var(--text)' },
  positive: { color: '#16a34a' },
  warning: { color: '#dc2626' },
  chartHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  chartTitle: { fontSize: '14px', fontWeight: 600, color: 'var(--text)' },
  toggle: { display: 'flex', gap: '4px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '3px' },
  toggleBtn: (active) => ({ padding: '4px 12px', fontSize: '12px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'DM Mono',monospace", background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-muted)' }),
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius)', padding: '12px 16px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' },
  chartWrap: { position: 'relative', height: '380px' },
};

function fmtMoney(v) { return '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function calcMonthlyPayment(principal, annualRate, years) {
  const r = annualRate / 12, n = years * 12;
  if (r <= 0 || n <= 0 || principal <= 0) return 0;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function calculate(form) {
  const age = parseFloat(form.current_age);
  const years = parseFloat(form.mortgage_years);
  const retireAge = parseFloat(form.retirement_age);
  const rate = parseFloat(form.interest_rate) / 100;
  const balance0 = parseFloat(form.outstanding_balance);
  const extraAmt = parseFloat(form.extra_payment) || 0;
  const lump = parseFloat(form.lump_sum) || 0;
  const freq = form.payment_frequency;

  const monthly = calcMonthlyPayment(balance0, rate, years);

  // Standard schedule
  let bal = balance0, totalIntStd = 0, paidOffStd = age;
  const stdSched = [{ age, balance: bal }];
  for (let m = 1; m <= years * 12; m++) {
    const a = age + m / 12;
    const int = bal * (rate / 12);
    let prin = monthly - int;
    if (prin > bal) prin = bal;
    bal -= prin; totalIntStd += int;
    if (m % 12 === 0 || bal <= 0) stdSched.push({ age: Math.round(a * 10) / 10, balance: Math.max(0, bal) });
    if (bal <= 0) { paidOffStd = Math.round(a * 10) / 10; break; }
  }

  // Extra schedule
  const extraMonthly = freq === 'weekly' ? extraAmt * 52 / 12 : freq === 'yearly' ? extraAmt / 12 : extraAmt;
  let balE = Math.max(0, balance0 - lump), totalIntExtra = 0, paidOffExtra = age;
  const extraSched = [{ age, balance: balE }];
  if (balE <= 0) {
    paidOffExtra = age;
  } else {
    for (let m = 1; m <= years * 12; m++) {
      const a = age + m / 12;
      const int = balE * (rate / 12);
      let prin = monthly - int;
      const ex = a < retireAge ? extraMonthly : 0;
      let total = prin + ex;
      if (total > balE) total = balE;
      balE -= total; totalIntExtra += int;
      if (m % 12 === 0 || balE <= 0) extraSched.push({ age: Math.round(a * 10) / 10, balance: Math.max(0, balE) });
      if (balE <= 0) { paidOffExtra = Math.round(a * 10) / 10; break; }
    }
  }

  // Retirement balance (standard)
  let retireBal = 0;
  for (const e of stdSched) { if (e.age >= retireAge) { retireBal = e.balance; break; } }

  const interestSaved = Math.max(0, totalIntStd - totalIntExtra);

  return {
    stdSched, extraSched,
    totalStandard: balance0 + totalIntStd,
    totalExtra: (balance0 - lump) + totalIntExtra + lump,
    interestSaved,
    paidOffStd, paidOffExtra,
    retireBal, monthly,
  };
}

export default function MortgageCalculator() {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);

  const [form, setForm] = useState({
    current_age: '', mortgage_years: '', retirement_age: '', interest_rate: '',
    outstanding_balance: '', extra_payment: '', payment_frequency: 'monthly', lump_sum: '',
  });
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const [chartType, setChartType] = useState('line');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = [];
    if (!form.current_age || isNaN(form.current_age)) errs.push('Current age is required.');
    if (!form.mortgage_years || isNaN(form.mortgage_years)) errs.push('Mortgage years is required.');
    if (!form.retirement_age || isNaN(form.retirement_age)) errs.push('Retirement age is required.');
    if (!form.interest_rate || isNaN(form.interest_rate)) errs.push('Interest rate is required.');
    if (!form.outstanding_balance || isNaN(form.outstanding_balance)) errs.push('Outstanding balance is required.');
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setResults(calculate(form));
  };

  useEffect(() => {
    if (!results || !canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    const HC = window.Chart;
    if (!HC) return;

    const ages = results.stdSched.map(e => e.age);
    const stdBal = results.stdSched.map(e => e.balance);

    // Interpolate extra balances onto standard age points
    const extraBal = [];
    let zeroFound = false;
    for (const age of ages) {
      if (zeroFound) { extraBal.push(null); continue; }
      const match = results.extraSched.find(e => e.age === age);
      if (match) { extraBal.push(match.balance <= 0 ? (zeroFound = true, 0) : match.balance); continue; }
      const before = [...results.extraSched].reverse().find(e => e.age < age);
      const after = results.extraSched.find(e => e.age > age);
      if (before && after) {
        const ratio = (age - before.age) / (after.age - before.age);
        extraBal.push(Math.max(0, before.balance + ratio * (after.balance - before.balance)));
      } else { extraBal.push(before ? before.balance : null); }
    }

    const isBar = chartType === 'bar';
    chartRef.current = new HC(canvasRef.current, {
      type: chartType,
      data: {
        labels: ages,
        datasets: [
          { label: 'Standard Repayment', data: stdBal, borderColor: '#7c3aed', backgroundColor: isBar ? 'rgba(124,58,237,0.7)' : 'rgba(124,58,237,0.1)', fill: !isBar, tension: 0.1 },
          { label: 'With Extra Payments', data: extraBal, borderColor: '#16a34a', backgroundColor: isBar ? 'rgba(22,163,74,0.7)' : 'rgba(22,163,74,0.1)', fill: !isBar, tension: 0.1 },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          x: { title: { display: true, text: 'Age' } },
          y: { title: { display: true, text: 'Mortgage Balance ($)' }, ticks: { callback: v => '$' + new Intl.NumberFormat().format(v) } }
        },
        plugins: { tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': $' + new Intl.NumberFormat().format(ctx.raw) } } }
      }
    });
  }, [results, chartType]);

  return (
    <div style={S.page}>
      <Navbar />

      <div style={S.content}>
        <h1 style={S.title}>Mortgage Repayment Calculator</h1>

        <div style={S.card}>
          <form onSubmit={handleSubmit}>
            <div style={S.grid}>
              {[
                { name: 'current_age',        label: 'Current Age',              type: 'number' },
                { name: 'mortgage_years',      label: 'Years Left on Mortgage',   type: 'number' },
                { name: 'retirement_age',      label: 'Retirement Age',           type: 'number' },
                { name: 'interest_rate',       label: 'Annual Interest Rate (%)', type: 'number', step: '0.01' },
                { name: 'outstanding_balance', label: 'Outstanding Balance ($)',  type: 'number', step: '1000' },
                { name: 'extra_payment',       label: 'Extra Payment ($)',        type: 'number', step: '10' },
                { name: 'lump_sum',            label: 'Initial Lump Sum ($)',     type: 'number', step: '100' },
              ].map(f => (
                <div key={f.name} style={S.group}>
                  <label style={S.label}>{f.label}</label>
                  <input style={S.input} type={f.type} name={f.name} value={form[f.name]} onChange={handleChange} step={f.step || '1'} />
                </div>
              ))}
              <div style={S.group}>
                <label style={S.label}>Extra Payment Frequency</label>
                <select style={S.select} name="payment_frequency" value={form.payment_frequency} onChange={handleChange}>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div style={S.btnRow}>
              <button type="submit" style={S.btn}>Calculate</button>
            </div>
          </form>
        </div>

        {errors.length > 0 && (
          <div style={S.error}>{errors.map((e, i) => <div key={i}>{e}</div>)}</div>
        )}

        {results && (
          <div style={S.card}>
            <div style={S.summaryGrid}>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Monthly Payment</div><div style={S.summaryValue}>{fmtMoney(results.monthly)}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Standard Payoff Age</div><div style={S.summaryValue}>{results.paidOffStd}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Extra Payoff Age</div><div style={S.summaryValue}>{results.paidOffExtra}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Years Saved</div><div style={{ ...S.summaryValue, ...S.positive }}>{Math.max(0, results.paidOffStd - results.paidOffExtra).toFixed(1)}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Total Standard Cost</div><div style={S.summaryValue}>{fmtMoney(results.totalStandard)}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Total Cost with Extra</div><div style={S.summaryValue}>{fmtMoney(results.totalExtra)}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Interest Saved</div><div style={{ ...S.summaryValue, ...S.positive }}>{fmtMoney(results.interestSaved)}</div></div>
              <div style={S.summaryItem}><div style={S.summaryLabel}>Mortgage at Retirement</div><div style={{ ...S.summaryValue, ...(results.retireBal > 0 ? S.warning : S.positive) }}>{fmtMoney(results.retireBal)}</div></div>
            </div>

            <div style={S.chartHeader}>
              <span style={S.chartTitle}>Mortgage Balance Over Time</span>
              <div style={S.toggle}>
                <button style={S.toggleBtn(chartType === 'line')} onClick={() => setChartType('line')}>LINE</button>
                <button style={S.toggleBtn(chartType === 'bar')}  onClick={() => setChartType('bar')}>BAR</button>
              </div>
            </div>
            <div style={S.chartWrap}>
              <canvas ref={canvasRef} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
